/**
 * План-карточка поля — мост «бюджет → поле» (экраны 1-3 прототипа
 * `docs/superpowers/prototypes/2026-08-08-budget-to-field/`).
 *
 * Дыра, которую она закрывает: планирование сделано целиком, карточка поля
 * сделана целиком, а связки не было — агроном открывал поле и видел пустоту
 * там, где холдинг уже посчитал культуру, урожайность и техкарту.
 *
 * Три вещи на экране:
 *   1. что по плану на этом поле (культура, урожайность, валовка, техкарта);
 *   2. во что развернётся техкарта — список операций с агроокнами и площадью
 *      ЭТОГО поля (раскрывается, чтобы не давить 14 строк сразу);
 *   3. вилка агронома: «Посеять по плану» (форма заполнена планом) либо
 *      «Посеять иначе» (обычный мастер + видимое расхождение с бюджетом).
 *
 * Чего здесь НЕТ и почему: кнопки «Развернуть в операции» (экран 3 прототипа).
 * Она пишет плановые операции в наряд и требует ответа founder по развилкам
 * Р3-3/Р3-5 — это следующий шаг моста, а не хвост этого.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp, Info, Sprout } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TileGrid } from '@/components/ui/tile-grid';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFieldSeasonPlan } from './api';
import { planVariantLink, techCardLink } from './planPrefill';

interface Props {
  fieldId: string;
  seasonId: string | null;
  canWrite: boolean;
  /** «Посеять по плану» — мастер сева, заполненный планом. */
  onSowByPlan: () => void;
  /** «Посеять иначе» — тот же мастер, но с видимым расхождением с бюджетом. */
  onSowOtherwise: () => void;
}

const num = (v: number | null | undefined, digits = 1): string =>
  v == null ? '—' : v.toLocaleString('ru-RU', { maximumFractionDigits: digits });

/** Деньги — «18,4 млн ₽» вместо 18 400 000: цифру читают взглядом, не считают. */
function money(v: number | null): string {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000) return `${num(v / 1_000_000, 1)} млн ₽`;
  if (Math.abs(v) >= 1_000) return `${num(v / 1_000, 0)} тыс ₽`;
  return `${num(v, 0)} ₽`;
}

function period(from: string | null, to: string | null): string {
  const d = (s: string | null) =>
    s ? s.split('-').reverse().slice(0, 2).join('.') + '.' + s.slice(0, 4) : null;
  const a = d(from);
  const b = d(to);
  if (a && b) return `${a} — ${b}`;
  return a ?? b ?? 'срок не задан';
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export function SeasonPlanCard({
  fieldId,
  seasonId,
  canWrite,
  onSowByPlan,
  onSowOtherwise,
}: Props) {
  const [opsOpen, setOpsOpen] = useState(false);
  const plan = useFieldSeasonPlan(fieldId, seasonId);

  if (!seasonId) return null;
  if (plan.isLoading) return <Skeleton className="h-40 w-full" />;
  // Ошибку не показываем красным: поле работает и без плана, а алый блок
  // сверху читается как сломанная карточка поля целиком. Но и молчать нельзя:
  // отсутствие блока агроном прочтёт как «плана нет», хотя план есть — ровно
  // то, против чего в этой же ручке заведён `empty_reason`.
  //
  // 403 — исключение: чужое хозяйство (ADR-072) молчит намеренно, сообщать о
  // существовании чужого плана мы не должны.
  if (plan.isError || !plan.data) {
    const status = (plan.error as { status?: number } | null)?.status;
    if (!plan.isError || status === 403 || status === 404) return null;
    return (
      <p className="py-2 text-sm text-muted-foreground">
        План сезона сейчас не загрузился — это не значит, что его нет. Обновите
        страницу или откройте поле позже.
      </p>
    );
  }

  const p = plan.data;

  if (!p.has_plan) {
    return (
      <Card>
        <CardHeader className="p-4 pb-3">
          <CardTitle className="text-base">План на сезон {p.season_year ?? ''}</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {p.empty_reason ?? 'Плана на это поле нет — сеем по решению агронома.'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const cardLink = techCardLink(p);
  const variantLink = planVariantLink(p);
  const variantBadge = p.variant?.is_approved ? (
    <Badge variant="primary">Утверждён в бюджете {p.season_year}</Badge>
  ) : (
    <Badge variant="outline">Вариант плана «{p.variant?.name}»</Badge>
  );

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sprout className="h-4 w-4 text-primary" />
              {p.crop_name ?? 'Занятие по плану'}
              {/* Сорт рядом с культурой, а не отдельной плиткой: агроном читает
                  «Подсолнечник · НК Брио» одной строкой. Бюджет мог сорт не
                  задать — тогда его просто нет, прочерк тут не нужен. */}
              {p.variety_name ? ` · ${p.variety_name}` : ''}
              {/* Пар посевом не является: «Пар чистый · плановый посев» —
                  бессмыслица, которую агроном читает как ошибку системы. */}
              {p.is_fallow ? ' · по плану' : ' · плановый посев'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {num(p.area_ha)} га
              {p.predecessor_label ? ` · предшественник — ${p.predecessor_label}` : ''}
            </p>
          </div>
          {/* Бейдж варианта — он же ссылка «откуда этот план»: вопрос «кто так
              решил» возникает ровно здесь, и ответ должен быть в одном клике.
              Нет хозяйства или года — остаётся неактивным бейджем. */}
          <div className="flex flex-wrap items-center gap-2">
            {variantLink ? (
              <Link
                {...variantLink}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={`Открыть вариант плана «${p.variant?.name}»`}
              >
                {variantBadge}
              </Link>
            ) : (
              variantBadge
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        {/* У пара нет ни урожайности, ни валовки, ни техкарты — по устройству
            модели (`tech_cards.crop_id NOT NULL`), а не потому что «не
            заполнили». Три прочерка подряд читаются как потерянные данные,
            поэтому у пара свой — короткий и честный — набор плиток. */}
        {p.is_fallow ? (
          <TileGrid cols={2}>
            <Tile label="Площадь по плану" value={`${num(p.area_ha)} га`} />
            <Tile
              label="Занятие по плану"
              value={p.crop_name ?? 'Пар'}
              hint="поле в программе сезона не засевается"
            />
          </TileGrid>
        ) : (
          <TileGrid cols={4}>
            <Tile label="Площадь по плану" value={`${num(p.area_ha)} га`} />
            <Tile
              label="Урожайность плановая"
              value={p.planned_yield_c_ha != null ? `${num(p.planned_yield_c_ha)} ц/га` : '—'}
              hint="бункерный вес"
            />
            <Tile
              label="Валовой сбор"
              value={p.gross_t != null ? `${num(p.gross_t, 0)} т` : '—'}
            />
            <Tile
              label="Техкарта"
              value={p.tech_card ? `${p.tech_card.operations_count} операций` : '—'}
              hint={p.tech_card?.name ?? p.tech_card_gap ?? undefined}
            />
          </TileGrid>
        )}

        {p.with_money && p.tech_card?.cost_total != null && (
          <div className="rounded-lg bg-primary/10 px-3 py-3 text-sm text-primary">
            Затраты по плану: {money(p.tech_card.cost_total)}
            {p.tech_card.cost_per_ha != null
              ? ` · ${num(p.tech_card.cost_per_ha, 0)} ₽/га`
              : ''}
          </div>
        )}

        {p.tech_card_gap && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{p.tech_card_gap}</AlertDescription>
          </Alert>
        )}

        {p.other_plan_sowings > 0 && (
          <p className="text-xs text-muted-foreground">
            В плане на это поле ещё {p.other_plan_sowings} занятие(-я) — план заведён по
            зонам. Здесь показано занимающее бо́льшую площадь.
          </p>
        )}

        {p.operations.length > 0 && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpsOpen((v) => !v)}
                aria-expanded={opsOpen}
              >
                {opsOpen ? (
                  <ChevronUp className="mr-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="mr-2 h-4 w-4" />
                )}
                {opsOpen ? 'Свернуть операции' : `Что в техкарте · ${p.operations.length}`}
              </Button>
              {/* Именно роутерный Link, а не <a href>: голая ссылка в SPA
                  перезагружает приложение целиком — агроном теряет вкладку
                  карточки и ждёт холодный старт вместо перехода. */}
              {cardLink && (
                <Button variant="ghost" size="sm" asChild>
                  <Link {...cardLink}>Посмотреть техкарту</Link>
                </Button>
              )}
            </div>
            {opsOpen && (
              <div className="rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Тех.операция</TableHead>
                      <TableHead>Плановое окно</TableHead>
                      <TableHead className="text-right">Площадь, га</TableHead>
                      {p.with_money && <TableHead className="text-right">₽/га</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {p.operations.map((op) => (
                      <TableRow key={op.operation_id}>
                        <TableCell>
                          {op.name}
                          {op.is_wip && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              под урожай {p.season_year}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {period(op.window_starts_on, op.window_ends_on)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {num(op.area_ha)}
                        </TableCell>
                        {p.with_money && (
                          <TableCell className="text-right tabular-nums">
                            {op.cost_per_ha != null ? num(op.cost_per_ha, 0) : '—'}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Это план холдинга: операций на поле он сам по себе не создаёт — сроки и
              площади показаны, чтобы было видно, что и когда предстоит.
            </p>
          </div>
        )}

        {p.materials.length > 0 && opsOpen && (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Потребность в ТМЦ на это поле
            </div>
            <ul className="space-y-1 text-sm">
              {p.materials.map((m, i) => (
                <li
                  key={m.budget_item_id ?? `m-${i}`}
                  className="flex flex-wrap items-baseline justify-between gap-2"
                >
                  <span>{m.name ?? 'позиция без названия'}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {m.qty != null ? num(m.qty, 1) : (m.gap ?? '—')}
                    {p.with_money && m.amount != null ? ` · ${money(m.amount)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {canWrite && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
            {/* У пара вилки «по плану / иначе» нет: подставлять в мастер
                нечего (ни культуры, ни густоты, ни агроокна), а «Посеять по
                плану» на непосевном занятии — обещание, которое форма не
                выполнит. Остаётся один честный вход: занять поле руками, и
                если это будет культура — расхождение с программой мастер
                покажет сам. */}
            {p.is_fallow ? (
              <>
                <Button variant="outline" onClick={onSowOtherwise}>
                  Занять поле
                </Button>
                <span className="text-xs text-muted-foreground">
                  По плану поле под паром — посев станет отклонением от программы.
                </span>
              </>
            ) : (
              <>
                <Button onClick={onSowByPlan}>Посеять по плану</Button>
                <Button variant="outline" onClick={onSowOtherwise}>
                  Посеять иначе
                </Button>
                <span className="text-xs text-muted-foreground">
                  {p.variant?.is_approved
                    ? 'Утверждённый бюджет с поля не переписывается — сев иначе останется отклонением.'
                    : 'План ещё не утверждён как программа сезона.'}
                </span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
